import React from 'react';
import ReactDOM from 'react-dom/client';

import { AxiosError } from 'axios';
import i18next from 'i18next';
import { closeSnackbar, enqueueSnackbar, SnackbarKey, SnackbarProvider, VariantType } from 'notistack';

import { ObjectInterface } from '@interfaces';

import { escapeRegExp } from './strings';

// add a <div> child to body under which to mount the snackbars
const mountPoint = document.createElement('snackbar-helper');
document.body.appendChild(mountPoint);
const root = ReactDOM.createRoot(mountPoint);
root.render(
  // see https://github.com/iamhosseindhv/notistack#snackbarprovider
  <SnackbarProvider dense maxSnack={5} anchorOrigin={{ horizontal: 'center', vertical: 'top' }} autoHideDuration={5000} preventDuplicate={true} />,
);

const Action = (snackbarId?: SnackbarKey) => (
  <>
    <button
      onClick={() => {
        closeSnackbar(snackbarId);
      }}
      className="mr-2 p-1 rounded hover:underline hover:bg-red-900"
    >
      Dismiss
    </button>
  </>
);

export const showToast = (msg: string | React.ReactNode, variant: VariantType = 'default', exts: any = {}) => {
  if (!msg) {
    return;
  }
  const options = {
    variant,
    action: Action,
    ...exts,
  };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return enqueueSnackbar(msg, options);
};
export const parseErrorMessage = (error: any, defaultMsg?: string) => {
  let msg = typeof error === 'string' ? error : defaultMsg || 'Internal Error!';
  if (error instanceof AxiosError) {
    const data = error?.response?.data;
    console.log(error, data);
    if (data && typeof data === 'string') {
      msg = data;
    } else if (data && data.message) {
      msg = data.message;
      if (Array.isArray(data.message) && data.error) {
        msg = data.error + ':\n' + data.message.join('\n');
      }
      if (Array.isArray(msg)) {
        msg = msg.join('\n');
      }
      if (msg && data.parameters) {
        msg = i18next.t(msg);
        let parameters: ObjectInterface = Array.isArray(data.parameters) ? {} : data.parameters;
        if (Array.isArray(data.parameters)) {
          parameters = data.parameters.reduce((obj: any, value: any, key: number) => {
            obj[(1 + key).toString()] = value;
            return obj;
          }, {});
        }
        Object.keys(parameters)
          .sort((a, b) => {
            if (String(a).length === String(b).length) {
              return String(b).localeCompare(String(a));
            }
            return String(b).length - String(a).length;
          })
          .forEach((key) => {
            const pattern = escapeRegExp(`%${key}`);
            const regx = new RegExp(`${pattern}([^a-zA-Z0-9]|$)`, 'gu');
            msg = msg.replace(regx, `${String(parameters[key]?.toString() || '')}$1`);
          });
      }
    }
  } else if (error instanceof Error) {
    msg = error.message || error.name || error.toString();
  }
  return msg || defaultMsg;
};
export const toast = Object.assign(showToast, {
  message: (msg: string | React.ReactNode, type: VariantType = 'default', options?: any) => showToast(msg, type, options),
  success: (msg: string | React.ReactNode, options?: any) => showToast(msg, 'success', options),
  warning: (msg: string | React.ReactNode, options?: any) => showToast(msg, 'warning', options),
  info: (msg: string | React.ReactNode, options?: any) => showToast(msg, 'info', options),
  error: (msg: string | React.ReactNode, options?: any) => showToast(msg, 'error', options),
  parseErrorMessage,
  processError: (error: any, defaultMsg?: string, options?: any) => {
    const msg = parseErrorMessage(error, defaultMsg);
    if (msg) {
      return showToast(msg, 'error', options);
    }
  },
  processMessageResponse: (res: { messages: { message: string; type: VariantType | string }[] } & any) => {
    const { messages } = res;
    if (Array.isArray(messages)) {
      messages.forEach(({ message, type }) => {
        switch (type) {
          case 'danger':
          case 'error':
            toast.error(String(message));
            break;
          case 'success':
            toast.success(String(message));
            break;
          case 'info':
            toast.info(String(message));
            break;
          case 'warning':
            toast.warning(String(message));
            break;
          default:
            toast.message(String(message));
        }
      });
    }
  },
  process: (res: { error?: string[]; success?: string[] } & any) => {
    const { error, success } = res;
    if (Array.isArray(success)) {
      success.forEach((msg: string) => {
        toast.success(msg);
      });
    } else if (typeof success === 'string') {
      toast.success(success);
    }
    if (Array.isArray(error)) {
      error.forEach((msg: string) => {
        toast.error(msg);
      });
    } else if (typeof error === 'string') {
      toast.error(error);
    }
  },
});

export default toast;
